import { Eventcalendar, MbscCalendarEventData, MbscDatepickerChangeEvent, MbscDatepickerPageChangeEvent, MbscSelectedDateChangeEvent } from "@mobiscroll/react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Event, EventType } from "../../interfaces/event";
import classnames from "classnames";
import { ReactComponent as OutlookCalendar } from "../../assets/img/outlook-calendar.svg";
import "../../assets/scss/custom/_custom.scss";
import { UserEventsResult } from "appointment-experience-client";
import { createAppointmentApi } from "../../api-helpers/AppointmentApiHelper";
import { UsersDto } from "users-experience-client";
import { DateTime } from "luxon";
import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { WorkflowConstants, WorkflowStateConstants } from "../../helper/WorkflowConstants";
import { GridDataRequest } from "patients-experience-client";
import {
  getTzIdentifier,
  TimeZoneKey,
} from "../../helper/momentTimezoneMapping";
import Spinner from "../../components/Spinner";
interface Props {
  user: UsersDto;
  selectedDate: Date;
  viewType?: "day" | "week" | "month";
  updateSelectedDate: (date: Date) => void;
}

interface CustomEvent extends MbscCalendarEventData {
  description: Date;
}

const AppointmentEventCalendar = ({ user, selectedDate, viewType, updateSelectedDate }: Props) => {
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedWeekRange, setSelectedWeekRange] = useState<{
    start: Date;
    end: Date;
  } | null>({
    start: selectedDate,
    end: selectedDate,
  });

  const currentDate = new Date(new Date().setHours(0, 0, 0, 0))

  const appointmentApi = createAppointmentApi();
  const workflowApi = createWorkflowApi();

  const handleSelectedDateChange = (args: MbscSelectedDateChangeEvent) => {
    const newDate = args.date as Date;
    updateSelectedDate(newDate);
  };

  const customEventRender = (event: CustomEvent) => {
    return (
      <div
        className={classnames("event-link h-100", {
          disabled: event?.original?.type === EventType.Outlook,
        })}
      >
        <div
          className={classnames(
            "event-container",
            `${event?.original?.type}-event`
          )}
        >
          <div className="event-header">
            <div className="event-title">
              <div className="event-time">
                <span className="event-title-icon">
                  {" "}
                  {event?.original?.type === EventType.Outlook && (
                    <OutlookCalendar />
                  )}{" "}
                </span>
                {event.start} - {event.end} - {event.title}
              </div>
            </div>
          </div>
          <div className="event-description">{event.original?.description}</div>
        </div>
      </div>
    );
  };
  const getUserCalendarAppointments = async (
    userEmail: string,
    startDateUTC: string,
    endDateUTC: string
  ) => {
    try {
      const response = await appointmentApi.appointmentControllerGetUserEvents({
        userEmail: userEmail,
        startTime: new Date(startDateUTC),
        endTime: new Date(endDateUTC),
      });

      const scheduleInformation = response.data as UserEventsResult[];
      console.log(scheduleInformation);
      return scheduleInformation ?? [];
    } catch (error) {
      console.error("Error fetching data: ", error);
      return [];
    }
  };

  const populateScheduleItems = (
    scheduleItem: UserEventsResult,
    idx: number
  ) => {
    if (scheduleItem.start?.dateTime && scheduleItem.end?.dateTime) {

      const startDateInCoachTimeZone = DateTime.fromISO(
        scheduleItem.start.dateTime as unknown as string, {zone: 'utc'}
      ).setZone(getTzIdentifier(user.timezone as TimeZoneKey));

      const endDateInCoachTimeZone = DateTime.fromISO(
        scheduleItem.end.dateTime as unknown as string, {zone: 'utc'}
      ).setZone(getTzIdentifier(user.timezone as TimeZoneKey));

      const start = new Date(
        startDateInCoachTimeZone.year,
        startDateInCoachTimeZone.month - 1,
        startDateInCoachTimeZone.day,
        startDateInCoachTimeZone.hour,
        startDateInCoachTimeZone.minute,
        startDateInCoachTimeZone.second,
        startDateInCoachTimeZone.millisecond
      );
      const end = new Date(
        endDateInCoachTimeZone.year,
        endDateInCoachTimeZone.month - 1,
        endDateInCoachTimeZone.day,
        endDateInCoachTimeZone.hour,
        endDateInCoachTimeZone.minute,
        endDateInCoachTimeZone.second,
        endDateInCoachTimeZone.millisecond
      );

      const diffInMillis = Math.abs(start.getTime() - end.getTime());
      const diffInMinutes = diffInMillis / (1000 * 60);

      personalCalendarEventCollection.push({
        id: scheduleItem.id ?? idx.toString(),
        start,
        end,
        title: scheduleItem.subject ?? "",
        description: diffInMinutes >= 30 ? "Outlook Calendar Event" : "",
        type: EventType.Outlook,
      });
    }
  };

  const personalCalendarEventCollection: Event[] = [];

  const loadData = async () => {
    setLoading(true);
    const startLocalDateTime = DateTime.fromJSDate(selectedWeekRange?.start!);
    const endLocalDateTime = DateTime.fromJSDate(selectedWeekRange?.end!);

    const startOfWeekISO = startLocalDateTime.startOf("week").toUTC().toISO();
    const endOfWeekISO = endLocalDateTime
      .endOf("week")
      .plus({ milliseconds: 1 })
      .toUTC()
      .toISO();

    const scheduleItems = await getUserCalendarAppointments(user.email, startOfWeekISO!, endOfWeekISO!) || [];
    scheduleItems.map((scheduleItem, idx) => {
      populateScheduleItems(scheduleItem, idx);
    });

    const request: GridDataRequest = {
      startRow: 0,
      endRow: Number.MAX_SAFE_INTEGER,
      sortCriteria: [
        {
          propertyName: "time",
          descending: false,
        },
      ],
      filters: [
        {
          propertyName: "time",
          operator: "greater-than-equals",
          propertyValue: new Date(startOfWeekISO!),
        },
        {
          propertyName: "time",
          operator: "less-than-equals",
          propertyValue: new Date(endOfWeekISO!),
        },
        {
          propertyName: "coachUserId",
          operator: "in",
          propertyValue: [user.id],
        },
      ],
    };

    try {
      const appointmentsGridData = await appointmentApi.appointmentControllerGetGridData(request);

      const workflowStates = (
        await workflowApi.workflowControllerGetStatesForWorkflow(
          WorkflowConstants.appointmentWorkflowType
        )
      ).data;

      const targetStateIds = workflowStates.filter((state) => 
        state.stateName !== WorkflowStateConstants.COMPLETED && 
        state.stateName !== WorkflowStateConstants.CANCELED
      ).map((state) => state.stateId);

      // Filter appointments with wfStateId matching one of the target state IDs
      const filteredAppointments = appointmentsGridData.data.data.filter(
        (appointment) => targetStateIds.includes(appointment.wfStateId)
      );
      let filteredCalendarEvent: Event[] = personalCalendarEventCollection;
      const formattedEvents = filteredAppointments.map((appointment) => {
        const dateInCoachTimeZone = DateTime.fromJSDate(
          new Date(appointment.time)
        ).setZone(getTzIdentifier(user.timezone as TimeZoneKey));

        const start = new Date(
          dateInCoachTimeZone.year,
          dateInCoachTimeZone.month - 1,
          dateInCoachTimeZone.day,
          dateInCoachTimeZone.hour,
          dateInCoachTimeZone.minute,
          dateInCoachTimeZone.second,
          dateInCoachTimeZone.millisecond
        );

        const end = new Date(
          start.getTime() + appointment.durationMinutes * 60000
        );
        // Filtering Outlook event to not show them double in the Appointments Calendar
        filteredCalendarEvent = filteredCalendarEvent.filter(
          (outlookAppointment) =>
            appointment.externalAppointmentId != outlookAppointment.id
        );
        return {
          id: appointment.appointmentId,
          start,
          end,
          title: appointment.patientName,
          description: appointment.appointmentType,
          type: EventType.Spire,
        };
      });
      setUserEvents([...formattedEvents, ...filteredCalendarEvent]);
      return appointmentsGridData.data;
    } catch (error) {
      console.error("Error loading data:", error);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user, selectedWeekRange]);

  return loading ? (
    <div className="flex justify-center items-center h-screen">
      <Spinner />
    </div>
  ) : (
    <div className="custom-event-calendar h-100">
      <Eventcalendar
        data={userEvents}
        min={currentDate}
        view={{
          calendar: {
            type: 'week', // Default view type
          }, 
          schedule: {
            type: viewType, // Use the passed or default viewType
            startDay: 1,
            endDay: 5,
            startTime: "07:00",
            endTime: "20:00",
            timeCellStep: 30,
            timeLabelStep: 30,
            allDay: false,
          },
        }}
        theme="ios"
        themeVariant="light"
        clickToCreate={false}
        dragToCreate={false}
        renderScheduleEvent={customEventRender}
        selectedDate={selectedDate} // Set the selected date
        onSelectedDateChange={handleSelectedDateChange}
        invalid={[
          {
            recurring: {
              repeat: 'weekly',
              weekDays: 'SA,SU', // Disable Saturday & Sunday
            },
          },
        ]}
        onPageChange={(args) => {
          setSelectedWeekRange({
            start: args.firstDay,
            end: args.lastDay,
          });
        }}
      />
    </div>
  );
};

export default AppointmentEventCalendar;
